/**
 * editorFocus.ts — deep-linking the Edit Resume lightbox to one finding.
 *
 * Recruiter Check findings carry a REC PATH (`experience[0].roles[1].bullets[2]`,
 * `skills[3]`, `summary`, `''` = resume-wide) plus the field the finding is
 * about. The editor has no per-item ids, so the deep link works in two steps:
 * derive the section's nav key from the path (always possible), then resolve
 * the item's TEXT from the loaded resume and find the element that renders it.
 *
 * Everything here is pure except the two DOM helpers at the bottom, which are
 * deliberately best-effort: a miss just means the user lands on the right
 * section, which is still the useful half of the feature.
 */
import type { ResumeJSON } from '../types';

/** One `name` or `name[3]` step of a rec path. */
export interface RecPathSegment {
  key: string;
  index: number | null;
}

/** Path heads that don't share a name with their editor nav key. */
const PATH_HEAD_TO_NAV_KEY: Record<string, string> = {
  summary_phrases: 'summary',
  name: 'basics',
};

/** Nav keys a rec path is allowed to resolve to (`data-nav-key` in the editor). */
const KNOWN_NAV_KEYS = new Set<string>([
  'basics', 'contact', 'summary', 'highlights', 'skills', 'experience',
  'education', 'awards', 'presentations', 'publications', 'portfolios',
  'coverletter', 'synonyms', 'demographics',
]);

const SEGMENT_RE = /^([A-Za-z_][A-Za-z0-9_]*)(?:\[(\d+)\])?$/;

/** Parse a rec path into segments; `null` for empty or malformed input. */
export function parseRecPath(path: string | null | undefined): RecPathSegment[] | null {
  const raw = (path || '').trim();
  if (!raw) return null;
  const segments: RecPathSegment[] = [];
  for (const part of raw.split('.')) {
    const m = SEGMENT_RE.exec(part.trim());
    if (!m) return null;
    segments.push({ key: m[1], index: m[2] === undefined ? null : Number(m[2]) });
  }
  return segments.length ? segments : null;
}

/** Editor nav key (`data-nav-key`) a finding's path belongs to, or `null`. */
export function navKeyForPath(path: string | null | undefined): string | null {
  const segments = parseRecPath(path);
  if (!segments) return null;
  const head = segments[0].key.toLowerCase();
  const key = PATH_HEAD_TO_NAV_KEY[head] || head;
  return KNOWN_NAV_KEYS.has(key) ? key : null;
}

/** Leaf fields tried, in order, when the finding doesn't name one. */
const DEFAULT_LEAF_FIELDS = ['text', 'title'];

/**
 * The text of the item a finding points at, read out of the loaded resume.
 * `null` whenever the path can't be walked (malformed, out of range, or the
 * leaf holds no usable string) — callers fall back to the finding's snippet.
 */
export function resolveFocusText(
  resume: ResumeJSON | null | undefined,
  path: string | null | undefined,
  field?: string | null,
): string | null {
  const segments = parseRecPath(path);
  if (!segments || !resume || typeof resume !== 'object') return null;

  let cursor: unknown = resume;
  for (const seg of segments) {
    if (!cursor || typeof cursor !== 'object' || Array.isArray(cursor)) return null;
    cursor = (cursor as Record<string, unknown>)[seg.key];
    if (seg.index !== null) {
      if (!Array.isArray(cursor) || seg.index >= cursor.length) return null;
      cursor = cursor[seg.index];
    }
  }

  if (typeof cursor === 'string') return cursor.trim() || null;
  if (!cursor || typeof cursor !== 'object' || Array.isArray(cursor)) return null;

  const leaf = cursor as Record<string, unknown>;
  const names = field ? [field, ...DEFAULT_LEAF_FIELDS] : DEFAULT_LEAF_FIELDS;
  for (const n of names) {
    const v = leaf[n];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Comparison form for matching resume text against what the DOM renders:
 * curly quotes and ellipses folded to ASCII, whitespace collapsed, lowercased.
 */
export function normalizeFocusText(raw: string | null | undefined): string {
  if (!raw) return '';
  return String(raw)
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/…/g, '...')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Strip the quotes and leading/trailing ellipsis a lint snippet is wrapped in. */
export function stripSnippetDecoration(raw: string | null | undefined): string {
  let s = (raw || '').trim();
  s = s.replace(/^[\s"'‘’“”]+/, '').replace(/[\s"'‘’“”]+$/, '');
  s = s.replace(/^(\.\.\.|…)\s*/, '').replace(/\s*(\.\.\.|…)$/, '');
  return s.trim();
}

/** Below this length a "match" is noise — half the card would contain it. */
const MIN_NEEDLE_LENGTH = 4;

/**
 * The smallest element inside `root` whose rendered text (or input value)
 * contains `needle`. Inputs win ties because they're the editable thing the
 * user came to change.
 */
export function findFocusElement(root: Element | null, needle: string): HTMLElement | null {
  const wanted = normalizeFocusText(needle);
  if (!root || wanted.length < MIN_NEEDLE_LENGTH) return null;

  const matches: { el: HTMLElement; size: number; isField: boolean }[] = [];
  const consider = (el: HTMLElement, text: string, isField: boolean) => {
    const norm = normalizeFocusText(text);
    if (norm && norm.includes(wanted)) matches.push({ el, size: norm.length, isField });
  };

  root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach((el) => consider(el, el.value, true));
  root.querySelectorAll<HTMLElement>('*')
    .forEach((el) => consider(el, el.textContent || '', false));

  if (!matches.length) return null;
  matches.sort((a, b) => (a.size - b.size) || (Number(b.isField) - Number(a.isField)));
  return matches[0].el;
}

/** How long the deep-link highlight stays on screen. */
const FLASH_MS = 2500;

/**
 * Ring the element the deep link landed on. Inline styles rather than utility
 * classes so the flash can't collide with (or be purged alongside) the card's
 * own Tailwind state classes; everything is restored when it fades.
 */
export function flashFocusElement(el: HTMLElement, ms: number = FLASH_MS): void {
  const prior = {
    outline: el.style.outline,
    outlineOffset: el.style.outlineOffset,
    boxShadow: el.style.boxShadow,
    borderRadius: el.style.borderRadius,
    transition: el.style.transition,
  };
  el.style.transition = 'outline-color 200ms ease, box-shadow 200ms ease';
  el.style.outline = '2px solid #f59e0b';
  el.style.outlineOffset = '2px';
  el.style.boxShadow = '0 0 0 6px rgba(245, 158, 11, 0.18)';
  if (!prior.borderRadius) el.style.borderRadius = '4px';
  setTimeout(() => {
    el.style.outline = prior.outline;
    el.style.outlineOffset = prior.outlineOffset;
    el.style.boxShadow = prior.boxShadow;
    el.style.borderRadius = prior.borderRadius;
    el.style.transition = prior.transition;
  }, ms);
}
